/**
 * @fileoverview Fetch Interceptor for WebZjs WASM
 *
 * The WebZjs WASM makes fetch() calls to lightwalletd but doesn't include
 * proper gRPC-web headers.  This interceptor:
 *   1. Adds gRPC-web headers for lightwalletd requests.
 *   2. Redirects JSON-RPC calls (meant for Zebra) that accidentally target
 *      lightwalletd to the configured Zebra RPC endpoint.
 *   3. Caches wallet-scanner notes and provides them as mock RPC responses
 *      when WASM queries for balance/UTXOs.
 *
 * Ported from WINBIT32 – URLs are now injectable via install().
 */

import {
	DEFAULT_ZEBRA_RPC_URL,
	DEFAULT_LIGHTWALLETD_URL,
} from './constants';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let interceptorInstalled = false;

/** Internal note cache keyed by address */
interface NoteCache {
	[address: string]: any[];
}
const noteCache: NoteCache = {};

// ---------------------------------------------------------------------------
// Public cache helpers
// ---------------------------------------------------------------------------

export const cacheNotesForAddress = (
	unifiedAddress: string,
	notes: any[],
	transparentReceiver?: string,
) => {
	noteCache[unifiedAddress] = notes;
	console.log(
		`📝 Cached ${notes.length} notes for ${unifiedAddress.substring(0, 20)}...`,
	);
	if (transparentReceiver) {
		noteCache[transparentReceiver] = notes;
		console.log(
			`📝 Also cached notes for transparent receiver: ${transparentReceiver}`,
		);
	}
};

export const clearNoteCacheForAddress = (
	unifiedAddress: string,
	transparentReceiver?: string,
) => {
	delete noteCache[unifiedAddress];
	console.log(
		`🗑️ Cleared note cache for ${unifiedAddress.substring(0, 20)}...`,
	);
	if (transparentReceiver) {
		delete noteCache[transparentReceiver];
		console.log(
			`🗑️ Also cleared transparent receiver cache: ${transparentReceiver}`,
		);
	}
};

// ---------------------------------------------------------------------------
// Interceptor install / uninstall
// ---------------------------------------------------------------------------

/**
 * Install a global `window.fetch` interceptor.
 *
 * @param opts.zebraRpcUrl  – Where to redirect JSON-RPC calls (default from constants).
 * @param opts.lightwalletdUrl – Used to match incoming requests for header injection.
 */
export function installFetchInterceptor(opts?: {
	zebraRpcUrl?: string;
	lightwalletdUrl?: string;
}) {
	if (typeof window === 'undefined') return;
	if (interceptorInstalled) {
		console.log('✅ Fetch interceptor already installed');
		return;
	}

	const zebraUrl = opts?.zebraRpcUrl ?? DEFAULT_ZEBRA_RPC_URL;
	const lwdHost = new URL(opts?.lightwalletdUrl ?? DEFAULT_LIGHTWALLETD_URL).host;

	// Save the current fetch (may already be wrapped by fetch-retry)
	const originalFetch = window.fetch.bind(window);
	const windowAny = window as any;
	if (!windowAny.__WB32_NATIVE_FETCH__) {
		windowAny.__WB32_NATIVE_FETCH__ = originalFetch;
	}

	window.fetch = async function (
		...args: Parameters<typeof fetch>
	): Promise<Response> {
		let [url, init] = args;
		const urlString =
			url instanceof Request ? url.url : url.toString();

		// ── lightwalletd interception ──────────────────────────────────
		if (urlString.includes(lwdHost) && !urlString.includes('rpc.zcash')) {
			let shouldRedirectToZebra = false;

			// Detect JSON-RPC bodies that should go to Zebra
			if (init?.method === 'POST' && init?.body) {
				try {
					const bodyStr =
						typeof init.body === 'string' ? init.body : '';
					const bodyJson = JSON.parse(bodyStr);

					const zebraRpcMethods = [
						'getblockchaininfo',
						'getblockhash',
						'getblock',
						'getbestblockhash',
						'getblockcount',
						'getaddressbalance',
						'getaddressutxos',
						'z_validateaddress',
					];

					if (
						bodyJson.jsonrpc &&
						zebraRpcMethods.includes(bodyJson.method)
					) {
						console.log(
							`🔀 Redirecting JSON-RPC ${bodyJson.method} → Zebra`,
						);
						url = zebraUrl;
						shouldRedirectToZebra = true;
					}
				} catch {
					// Not JSON → gRPC-web; continue below
				}
			}

			if (!shouldRedirectToZebra) {
				// Second-pass: catch any JSON-RPC that slipped through
				let isJsonRpc = false;
				if (init?.body) {
					try {
						const bodyStr =
							typeof init.body === 'string' ? init.body : '';
						const bodyJson = JSON.parse(bodyStr);
						if (bodyJson.jsonrpc) {
							isJsonRpc = true;
							console.log(
								`⚠️ Redirecting unexpected JSON-RPC ${bodyJson.method} → Zebra`,
							);
							url = zebraUrl;
							shouldRedirectToZebra = true;
						}
					} catch {
						/* gRPC-web */
					}
				}

				if (!isJsonRpc) {
					// Genuine gRPC-web request → add required headers
					console.log(
						'🔧 Adding gRPC-web headers for lightwalletd request',
					);
					init = init || {};
					const headers = new Headers(init.headers || {});
					if (!headers.has('Content-Type'))
						headers.set('Content-Type', 'application/grpc-web+proto');
					if (!headers.has('Accept'))
						headers.set('Accept', 'application/grpc-web+proto');
					init.headers = headers;
					return originalFetch(url, init);
				}
			}
		}

		// ── Zebra RPC interception (balance / UTXO mocking) ───────────
		if (
			urlString.includes('rpc.zcash') ||
			url.toString().includes('rpc.zcash')
		) {
			if (init?.method === 'POST' && init?.body) {
				try {
					const bodyStr =
						typeof init.body === 'string' ? init.body : '';
					const bodyJson = JSON.parse(bodyStr);
					const method = bodyJson.method;
					const params = bodyJson.params;

					if (
						(method === 'getaddressbalance' ||
							method === 'getaddressutxos') &&
						params &&
						params[0]
					) {
						const addresses = params[0].addresses || [params[0]];
						const cachedAddr = addresses.find(
							(addr: string) => noteCache[addr],
						);

						if (cachedAddr && noteCache[cachedAddr]) {
							console.log(
								`🎯 Intercepting ${method} for cached address`,
							);
							const notes = noteCache[cachedAddr];
							const totalValue = notes.reduce(
								(sum: number, n: any) => sum + (n.value || 0),
								0,
							);

							let result: any;
							if (method === 'getaddressbalance') {
								result = {
									balance: totalValue,
									received: totalValue,
								};
							} else {
								result = notes.map(
									(note: any, index: number) => ({
										txid:
											note.commitment || '0'.repeat(64),
										outputIndex: index,
										script: '',
										satoshis: note.value || 0,
										height: note.block_height || 0,
									}),
								);
							}

							return new Response(
								JSON.stringify({
									jsonrpc: '2.0',
									id: bodyJson.id || 1,
									result,
								}),
								{
									status: 200,
									headers: {
										'Content-Type': 'application/json',
									},
								},
							);
						}
					}
				} catch {
					// pass-through
				}
			}
		}

		return originalFetch(url, init);
	};

	windowAny.__WB32_FETCH_INTERCEPTOR__ = window.fetch;
	interceptorInstalled = true;
	console.log(
		'✅ Fetch interceptor installed for WebZjs WASM gRPC-web compatibility',
	);
}

export function uninstallFetchInterceptor() {
	if (typeof window === 'undefined') return;
	const windowAny = window as any;
	if (windowAny.__WB32_NATIVE_FETCH__) {
		window.fetch = windowAny.__WB32_NATIVE_FETCH__;
	}
	interceptorInstalled = false;
	console.log('⚠️ Fetch interceptor uninstalled');
}

export function isInterceptorInstalled(): boolean {
	return interceptorInstalled;
}

